import React, { useState, useEffect, useCallback } from 'react';
import { T } from '../constants';
import { API_BASE } from '../constants';

const VERDICT_COLORS = {
  GO:      { bg: 'rgba(8,153,129,0.13)',  border: 'rgba(8,153,129,0.45)',  color: '#089981' },
  CAUTION: { bg: 'rgba(224,155,0,0.13)',  border: 'rgba(224,155,0,0.45)',  color: '#d4920a' },
  BLOCK:   { bg: 'rgba(242,54,69,0.13)',  border: 'rgba(242,54,69,0.45)',  color: '#f23645' },
  SKIP:    { bg: 'rgba(120,120,120,0.13)', border: 'rgba(120,120,120,0.45)', color: '#888'   },
};
const VERDICT_LABEL = { GO: 'GO', CAUTION: 'WARN', BLOCK: 'BLOCK', SKIP: 'NOT REVIEWED' };

function todayStr() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD in ET
}

function VerdictPill({ verdict }) {
  if (!verdict) return <span style={{ color: T.textMuted, fontSize: 11 }}>—</span>;
  const c = VERDICT_COLORS[verdict] || {};
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
      padding: '2px 7px', borderRadius: 4,
      background: c.bg, border: `1px solid ${c.border}`, color: c.color,
    }}>
      {VERDICT_LABEL[verdict] || verdict}
    </span>
  );
}

// ─── Tab 1: Real-time Verdicts ───────────────────────────────

function VerdictsTab() {
  const [date, setDate]               = useState(todayStr());
  const [systemId, setSystemId]       = useState('');
  const [verdict, setVerdict]         = useState('');
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [systems, setSystems]         = useState([]);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const [counts, setCounts]           = useState({ qualified: 0, nonQualified: 0, audit: 0 });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ date, limit: 1000 });
      if (systemId)    params.set('system_id', systemId);
      if (verdict)     params.set('verdict', verdict);
      if (showSkipped) params.set('include_skipped', '1');
      const res = await fetch(`${API_BASE}/strategies/api/claude-review/verdicts/?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []);
      setCounts({
        qualified:    data.qualified_count     ?? 0,
        nonQualified: data.non_qualified_count ?? 0,
        audit:        data.audit_count         ?? 0,
      });
      const uniq = [...new Set((data.rows || []).map(r => r.system_id).filter(Boolean))].sort();
      if (uniq.length) setSystems(uniq);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [date, systemId, verdict, showSkipped]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(fetchRows, 60_000);
    return () => clearInterval(id);
  }, [fetchRows]);

  function downloadCSV() {
    const params = new URLSearchParams({ date, fmt: 'csv', limit: 0 });
    if (systemId)    params.set('system_id', systemId);
    if (verdict)     params.set('verdict', verdict);
    if (showSkipped) params.set('include_skipped', '1');
    window.location.href = `${API_BASE}/strategies/api/claude-review/verdicts/?${params}`;
  }

  // Full history — every trade with its AI review, ignoring the date picker.
  // Strategy / verdict / non-qualified filters still apply if set.
  function downloadAllCSV() {
    const params = new URLSearchParams({ all: 1, fmt: 'csv', limit: 0 });
    if (systemId)    params.set('system_id', systemId);
    if (verdict)     params.set('verdict', verdict);
    if (showSkipped) params.set('include_skipped', '1');
    window.location.href = `${API_BASE}/strategies/api/claude-review/verdicts/?${params}`;
  }

  async function runBackfill(force = false) {
    setBackfilling(true);
    setBackfillMsg(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/strategies/api/claude-review/backfill/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, force }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const reviewed     = data.total_reviewed ?? 0;
      const gated        = Object.values(data.results || {}).reduce((s, r) => s + (r.gated   || 0), 0);
      const alreadyDone  = Object.values(data.results || {}).reduce((s, r) => s + (r.skipped || 0), 0);
      if (reviewed > 0 || gated > 0) {
        const gatedNote = gated > 0 ? ` (${gated} non-qualified skipped by gate)` : '';
        setBackfillMsg({
          ok: true,
          text: `✓ Reviewed ${reviewed} qualified trade${reviewed !== 1 ? 's' : ''}${gatedNote}. Refreshing…`,
        });
        setTimeout(() => { fetchRows(); setBackfillMsg(null); }, 1500);
      } else if (alreadyDone > 0) {
        setBackfillMsg({ ok: false, text: `Already reviewed (${alreadyDone} existing verdicts). Use force to re-run.` });
      } else {
        setBackfillMsg({ ok: false, text: `No signals found for ${date}.` });
      }
    } catch (e) {
      setError(`Backfill failed: ${e.message}`);
    } finally {
      setBackfilling(false);
    }
  }

  const goCount      = rows.filter(r => r.verdict === 'GO').length;
  const cautionCount = rows.filter(r => r.verdict === 'CAUTION').length;
  const blockCount   = rows.filter(r => r.verdict === 'BLOCK').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Filters */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        padding: '12px 16px',
        background: 'var(--tv-surface)', border: '1px solid var(--tv-border)',
        borderRadius: 6,
      }}>
        <label style={{ fontSize: 11, color: T.textMuted }}>Date</label>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={inputStyle}
        />

        <label style={{ fontSize: 11, color: T.textMuted }}>Strategy</label>
        <select value={systemId} onChange={e => setSystemId(e.target.value)} style={inputStyle}>
          <option value="">All</option>
          {systems.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <label style={{ fontSize: 11, color: T.textMuted }}>Verdict</label>
        <select value={verdict} onChange={e => setVerdict(e.target.value)} style={inputStyle}>
          <option value="">All</option>
          <option value="GO">GO</option>
          <option value="CAUTION">CAUTION</option>
          <option value="BLOCK">BLOCK</option>
          <option value="SKIP">NOT REVIEWED</option>
        </select>

        <label style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 11, color: T.textMuted, cursor: 'pointer',
          padding: '4px 10px', borderRadius: 4,
          border: '1px solid var(--tv-border)',
        }}>
          <input
            type="checkbox"
            checked={showSkipped}
            onChange={e => setShowSkipped(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Show non-qualified
        </label>

        <button onClick={fetchRows} style={btnStyle('#3b82f6')}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>

        <button
          onClick={() => runBackfill(false)}
          disabled={backfilling}
          title="Retroactively run Claude AI review for all signals on this date"
          style={btnStyle('#7c3aed', backfilling)}
        >
          {backfilling ? 'Reviewing…' : '⚡ Run Historical Review'}
        </button>

        <div style={{ flex: 1 }} />

        <button onClick={downloadCSV} style={btnStyle('#4b5563')}
          title="Export the verdicts shown for the selected date">
          ↓ Download CSV (this date)
        </button>
        <button onClick={downloadAllCSV} style={btnStyle('#16a34a')}
          title="Export every trade with its AI review — full history, all dates">
          ↓ Download All History
        </button>
      </div>

      {/* Backfill result banner */}
      {backfillMsg && (
        <div style={{
          padding: '10px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600,
          background: backfillMsg.ok ? 'rgba(8,153,129,0.12)' : 'rgba(224,155,0,0.12)',
          border: `1px solid ${backfillMsg.ok ? 'rgba(8,153,129,0.4)' : 'rgba(224,155,0,0.4)'}`,
          color: backfillMsg.ok ? '#089981' : '#d4920a',
        }}>
          {backfillMsg.text}
        </div>
      )}

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Qualified Trades', value: counts.qualified,    color: T.text     },
          { label: 'GO',               value: goCount,             color: '#089981'  },
          { label: 'CAUTION',          value: cautionCount,        color: '#d4920a'  },
          { label: 'BLOCK',            value: blockCount,          color: '#f23645'  },
          { label: 'Audit',            value: counts.audit,        color: '#dc2626'  },
          { label: 'Not Reviewed',     value: counts.nonQualified, color: T.textMuted },
        ].map(s => (
          <div key={s.label} style={{
            padding: '10px 18px', borderRadius: 6, flex: '1 1 130px', minWidth: 0,
            background: 'var(--tv-surface)', border: '1px solid var(--tv-border)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color, fontFamily: 'Roboto Mono, monospace' }}>
              {s.value}
            </div>
            <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 12, background: 'rgba(242,54,69,0.1)', border: '1px solid rgba(242,54,69,0.3)', borderRadius: 6, fontSize: 12, color: '#f23645' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--tv-border)', borderRadius: 6 }}>
        <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--tv-surface-alt, var(--tv-surface))', borderBottom: '1px solid var(--tv-border)' }}>
              {['Time (ET)', 'Strategy', 'Direction', 'Action', 'Grade', 'Verdict', 'Score', 'Reasoning', 'Latency', 'Error'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
                No verdicts for {date}
              </td></tr>
            )}
            {rows.map((r, i) => {
              const isAudit = r.audit_flag === true;
              const isSkip  = r.verdict === 'SKIP';
              const rowBg   = isAudit
                ? 'rgba(220,38,38,0.10)'
                : isSkip
                  ? 'rgba(120,120,120,0.05)'
                  : (i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)');
              return (
                <tr key={r.id || i} style={{
                  borderBottom: '1px solid var(--tv-border)',
                  background:  rowBg,
                  borderLeft:  isAudit ? '3px solid #dc2626' : '3px solid transparent',
                  opacity:     isSkip && !isAudit ? 0.65 : 1,
                }}>
                  <td style={tdStyle}>
                    <span className="mono" style={{ fontSize: 11, color: T.textMuted }}>
                      {(r.display_ts_ny || r.signal_ts_ny || r.created_at_ny || '').slice(11, 19) || '—'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 600, color: T.text }}>{r.system_id}</span>
                    {isAudit && (
                      <span style={{
                        marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                        padding: '2px 6px', borderRadius: 3,
                        background: '#dc2626', color: 'white',
                      }}>
                        INCONSISTENT
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}><span style={{ fontSize: 11, color: r.direction === 'LONG' ? '#089981' : '#f23645' }}>{r.direction || '—'}</span></td>
                  <td style={tdStyle}><span style={{ fontSize: 11, color: T.textDim }}>{r.action || '—'}</span></td>
                  <td style={tdStyle}><span style={{ fontFamily: 'Roboto Mono, monospace', fontSize: 11, color: T.textDim }}>{r.grade || '—'}</span></td>
                  <td style={tdStyle}><VerdictPill verdict={r.verdict} /></td>
                  <td style={tdStyle}>
                    {isSkip
                      ? <span style={{ fontSize: 11, color: T.textMuted }}>—</span>
                      : <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: scoreColor(r.score) }}>{r.score ?? '—'}</span>
                    }
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', lineHeight: 1.5, color: T.textDim }}>
                    {isSkip ? (r.skip_reason || r.reasoning || '—') : (r.reasoning || '—')}
                  </td>
                  <td style={tdStyle}>
                    {isSkip
                      ? <span style={{ fontSize: 11, color: T.textMuted }}>—</span>
                      : <span className="mono" style={{ fontSize: 11, color: T.textMuted }}>{r.latency_ms != null ? `${r.latency_ms}ms` : '—'}</span>
                    }
                  </td>
                  <td style={tdStyle}><span style={{ fontSize: 10, color: r.error ? '#f23645' : T.textMuted }}>{r.error || '—'}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab 2: Daily Digest ──────────────────────────────────────

function DigestTab() {
  const [date, setDate]               = useState(todayStr());
  const [digest, setDigest]           = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [narrative, setNarrative]     = useState('');
  const [narLoading, setNarLoading]   = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  const fetchDigest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNarrative('');
    try {
      const params = new URLSearchParams({ date });
      if (showSkipped) params.set('include_skipped', '1');
      const res = await fetch(`${API_BASE}/strategies/api/claude-review/digest/?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDigest(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [date, showSkipped]);

  useEffect(() => { fetchDigest(); }, [fetchDigest]);

  async function generateNarrative() {
    setNarLoading(true);
    setNarrative('');
    try {
      const res = await fetch(`${API_BASE}/strategies/api/claude-review/narrative/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setNarrative(data.narrative || '');
    } catch (e) {
      setNarrative(`Error: ${e.message}`);
    } finally {
      setNarLoading(false);
    }
  }

  function downloadCSV() {
    const params = new URLSearchParams({ date, fmt: 'csv', limit: 0 });
    if (showSkipped) params.set('include_skipped', '1');
    window.location.href = `${API_BASE}/strategies/api/claude-review/verdicts/?${params}`;
  }

  function downloadAllCSV() {
    const params = new URLSearchParams({ all: 1, fmt: 'csv', limit: 0 });
    if (showSkipped) params.set('include_skipped', '1');
    window.location.href = `${API_BASE}/strategies/api/claude-review/verdicts/?${params}`;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Filters */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        padding: '12px 16px',
        background: 'var(--tv-surface)', border: '1px solid var(--tv-border)',
        borderRadius: 6,
      }}>
        <label style={{ fontSize: 11, color: T.textMuted }}>Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />

        <label style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 11, color: T.textMuted, cursor: 'pointer',
          padding: '4px 10px', borderRadius: 4,
          border: '1px solid var(--tv-border)',
        }}>
          <input
            type="checkbox"
            checked={showSkipped}
            onChange={e => setShowSkipped(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Show non-qualified
        </label>

        <button onClick={fetchDigest} style={btnStyle('#3b82f6')}>
          {loading ? 'Loading…' : 'Load Digest'}
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={downloadCSV} style={btnStyle('#4b5563')}
          title="Export verdicts for the selected date">↓ Download CSV (this date)</button>
        <button onClick={downloadAllCSV} style={btnStyle('#16a34a')}
          title="Export every trade with its AI review — full history">↓ Download All History</button>
      </div>

      {error && (
        <div style={{ padding: 12, background: 'rgba(242,54,69,0.1)', border: '1px solid rgba(242,54,69,0.3)', borderRadius: 6, fontSize: 12, color: '#f23645' }}>
          {error}
        </div>
      )}

      {digest && (
        <>
          {/* Summary cards — matches the Real-time tab so both stay in sync */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Qualified Trades', value: digest.qualified_count     ?? 0,                       color: T.text     },
              { label: 'GO',               value: digest.by_verdict?.GO      ?? 0,                       color: '#089981'  },
              { label: 'CAUTION',          value: digest.by_verdict?.CAUTION ?? 0,                       color: '#d4920a'  },
              { label: 'BLOCK',            value: digest.by_verdict?.BLOCK   ?? 0,                       color: '#f23645'  },
              { label: 'Audit',            value: digest.audit_count         ?? 0,                       color: '#dc2626'  },
              { label: 'Not Reviewed',     value: digest.non_qualified_count ?? 0,                       color: T.textMuted },
            ].map(s => (
              <div key={s.label} style={{
                padding: '10px 18px', borderRadius: 6, flex: '1 1 130px', minWidth: 0,
                background: 'var(--tv-surface)', border: '1px solid var(--tv-border)',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color, fontFamily: 'Roboto Mono, monospace' }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* By-strategy table */}
          {Object.keys(digest.by_system || {}).length > 0 && (
            <div style={{ background: 'var(--tv-surface)', border: '1px solid var(--tv-border)', borderRadius: 6 }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--tv-border)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textMuted }}>
                By Strategy
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '40%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--tv-border)' }}>
                      <th style={thStyle}>Strategy</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                      <th style={{ ...thStyle, textAlign: 'right', color: '#089981' }}>GO</th>
                      <th style={{ ...thStyle, textAlign: 'right', color: '#d4920a' }}>CAUTION</th>
                      <th style={{ ...thStyle, textAlign: 'right', color: '#f23645' }}>BLOCK</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Avg Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(digest.by_system).sort().map(([sys, s], i) => (
                      <tr key={sys} style={{ borderBottom: '1px solid var(--tv-border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <td style={tdStyle}><span style={{ fontWeight: 600, color: T.text }}>{sys}</span></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}><span className="mono">{s.count}</span></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}><span className="mono" style={{ color: '#089981' }}>{s.GO}</span></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}><span className="mono" style={{ color: '#d4920a' }}>{s.CAUTION}</span></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}><span className="mono" style={{ color: '#f23645' }}>{s.BLOCK}</span></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}><span className="mono" style={{ color: scoreColor(s.avg_score) }}>{s.avg_score}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* GO Signals — Claude-approved, clean rule-pass entries */}
          <div style={{ background: 'var(--tv-surface)', border: '1px solid var(--tv-border)', borderRadius: 6 }}>
            <div style={{
              padding: '10px 16px', borderBottom: '1px solid var(--tv-border)',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: T.textMuted,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ color: '#089981' }}>●</span>
              GO Signals
              <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}>
                ({digest.go_signals?.length ?? 0})
              </span>
            </div>
            {!digest.go_signals?.length ? (
              <div style={{ padding: '14px 16px', fontSize: 12, color: T.textMuted }}>
                No GO signals for {date}.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--tv-surface-alt, var(--tv-surface))', borderBottom: '1px solid var(--tv-border)' }}>
                      {['Time (ET)', 'Strategy', 'Direction', 'Action', 'Grade', 'Verdict', 'Score', 'Reasoning'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {digest.go_signals.map((r, i) => (
                      <tr key={i} style={{
                        borderBottom: i < digest.go_signals.length - 1 ? '1px solid var(--tv-border)' : 'none',
                        background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                        borderLeft: '3px solid #089981',
                      }}>
                        <td style={tdStyle}>
                          <span className="mono" style={{ fontSize: 11, color: T.textMuted }}>
                            {r.time_et ? r.time_et.slice(11, 19) : '—'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: 600, color: T.text }}>{r.system_id}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 11, color: r.direction === 'LONG' ? '#089981' : '#f23645' }}>
                            {r.direction || '—'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 11, color: T.textDim }}>{r.action || '—'}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontFamily: 'Roboto Mono, monospace', fontSize: 11, color: T.textDim }}>
                            {r.grade || '—'}
                          </span>
                        </td>
                        <td style={tdStyle}><VerdictPill verdict={r.verdict} /></td>
                        <td style={tdStyle}>
                          <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: scoreColor(r.score) }}>
                            {r.score ?? '—'}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, maxWidth: 380, whiteSpace: 'normal', lineHeight: 1.5, color: T.textDim }}>
                          {r.reasoning || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Flags — CAUTION/BLOCK + AUDIT pinned to top with red band */}
          <div style={{ background: 'var(--tv-surface)', border: '1px solid var(--tv-border)', borderRadius: 6 }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--tv-border)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textMuted }}>
              CAUTION / BLOCK / Audit Flags
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}>
                ({digest.flags?.length ?? 0})
              </span>
            </div>
            {!digest.flags?.length ? (
              <div style={{ padding: '14px 16px', fontSize: 12, color: '#089981' }}>
                Clean — all signals passed with GO verdict.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--tv-surface-alt, var(--tv-surface))', borderBottom: '1px solid var(--tv-border)' }}>
                      {['Time (ET)', 'Strategy', 'Direction', 'Action', 'Grade', 'Verdict', 'Score', 'Reasoning'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {digest.flags.map((f, i) => {
                      const isAudit = f.audit_flag === true;
                      const verdictColor = (VERDICT_COLORS[f.verdict] || {}).color || T.border;
                      const rowBg = isAudit
                        ? 'rgba(220,38,38,0.10)'
                        : (i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)');
                      return (
                        <tr key={i} style={{
                          borderBottom: i < digest.flags.length - 1 ? '1px solid var(--tv-border)' : 'none',
                          background: rowBg,
                          borderLeft: `3px solid ${isAudit ? '#dc2626' : verdictColor}`,
                        }}>
                          <td style={tdStyle}>
                            <span className="mono" style={{ fontSize: 11, color: T.textMuted }}>
                              {f.time_et ? f.time_et.slice(11, 19) : '—'}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: 600, color: T.text }}>{f.system_id}</span>
                            {isAudit && (
                              <span style={{
                                marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                                padding: '2px 6px', borderRadius: 3,
                                background: '#dc2626', color: 'white',
                              }}>
                                INCONSISTENT
                              </span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontSize: 11, color: f.direction === 'LONG' ? '#089981' : '#f23645' }}>
                              {f.direction || '—'}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontSize: 11, color: T.textDim }}>{f.action || '—'}</span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontFamily: 'Roboto Mono, monospace', fontSize: 11, color: T.textDim }}>
                              {f.grade || '—'}
                            </span>
                          </td>
                          <td style={tdStyle}><VerdictPill verdict={f.verdict} /></td>
                          <td style={tdStyle}>
                            <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: scoreColor(f.score) }}>
                              {f.score ?? '—'}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, maxWidth: 380, whiteSpace: 'normal', lineHeight: 1.5, color: T.textDim }}>
                            {f.reasoning || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Claude narrative */}
          <div style={{ background: 'var(--tv-surface)', border: '1px solid var(--tv-border)', borderRadius: 6 }}>
            <div style={{
              padding: '10px 16px', borderBottom: '1px solid var(--tv-border)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textMuted }}>
                Claude AI Assessment
              </span>
              <button
                onClick={generateNarrative}
                disabled={narLoading}
                style={btnStyle('#7c3aed', narLoading)}
              >
                {narLoading ? 'Generating…' : '✦ Generate Narrative'}
              </button>
            </div>
            <div style={{ padding: '14px 16px', fontSize: 13, color: narrative ? T.text : T.textMuted, lineHeight: 1.7, minHeight: 60 }}>
              {narrative || 'Click "Generate Narrative" to get a Claude AI day-end summary for this date.'}
            </div>
          </div>
        </>
      )}

      {!digest && !loading && !error && (
        <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
          Select a date and click Load Digest
        </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────

export default function AIReview({ onClose }) {
  const [tab, setTab] = useState('verdicts');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%', overflow: 'hidden' }}>
      {/* Page header */}
      <div style={{
        padding: '8px clamp(10px, 3vw, 20px)', minHeight: 48, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        background: 'var(--tv-surface)', borderBottom: '1px solid var(--tv-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6.5" stroke="#7c3aed" strokeWidth="1.4"/>
            <path d="M5 8.5 L7 10.5 L11 6" stroke="#7c3aed" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text, letterSpacing: '0.03em' }}>AI Review</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>Claude signal audit</span>
        </div>

        <div style={{ display: 'flex', gap: 2, marginLeft: 8, flexWrap: 'wrap' }}>
          {[
            { id: 'verdicts', label: 'Real-time Verdicts' },
            { id: 'digest',   label: 'Daily Digest' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '4px 14px', borderRadius: 4, border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
                background: tab === t.id ? 'rgba(124,58,237,0.15)' : 'transparent',
                color: tab === t.id ? '#7c3aed' : T.textMuted,
                borderBottom: tab === t.id ? '2px solid #7c3aed' : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        <button
          onClick={onClose}
          style={{
            padding: '4px 12px', borderRadius: 4, border: '1px solid var(--tv-border)',
            background: 'transparent', cursor: 'pointer',
            fontSize: 11, color: T.textMuted,
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Page body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'clamp(10px, 3vw, 20px)' }}>
        {tab === 'verdicts' ? <VerdictsTab /> : <DigestTab />}
      </div>
    </div>
  );
}

// ─── style helpers ────────────────────────────────────────────

function scoreColor(score) {
  if (score == null) return T.textMuted;
  if (score >= 75) return '#089981';
  if (score >= 50) return '#d4920a';
  return '#f23645';
}

const inputStyle = {
  padding: '4px 8px', borderRadius: 4, fontSize: 11,
  background: 'var(--tv-bg)', border: '1px solid var(--tv-border)',
  color: 'var(--tv-text)', outline: 'none',
};

function btnStyle(color, disabled = false) {
  return {
    padding: '5px 14px', borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
    background: disabled ? 'rgba(100,100,100,0.2)' : `${color}22`,
    color: disabled ? T.textMuted : color,
    border: `1px solid ${disabled ? 'transparent' : color + '55'}`,
    opacity: disabled ? 0.7 : 1,
  };
}

const thStyle = {
  padding: '8px 12px', textAlign: 'left',
  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: 'var(--tv-text-muted)', whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '8px 12px', verticalAlign: 'middle',
  fontSize: 12, color: 'var(--tv-text-dim)',
  whiteSpace: 'nowrap',
};
